import * as Sentry from '@sentry/react'

/**
 * Supabase auth error codes that represent expected user mistakes (wrong
 * password, email not yet confirmed, rate-limit hit, …). Filtering these out
 * keeps the Sentry feed focused on real bugs and avoids burning quota on
 * Login-Tippfehler.
 *
 * Anything not in this set — server misconfiguration ("Legacy API Keys are
 * disabled"), unexpected 5xx, schema drift — is reported. Transient network
 * failures are the one further exception; see `TRANSIENT_NETWORK_ERROR` below.
 */
const KNOWN_USER_ERROR_CODES: ReadonlySet<string> = new Set([
  'same_password',
  'session_not_found',
  'invalid_credentials',
  'email_not_confirmed',
  'over_email_send_rate_limit',
  'over_request_rate_limit',
  'user_already_exists',
  'weak_password',
  // Abgelaufener oder falsch getippter E-Mail-Code. Supabase meldet ihn
  // korrekt, die App zeigt das rote Feld — gemeldet wurde er trotzdem
  // (HAPPYTASK-1E, 21.09.2026, `verifyEmailOTP`). Dasselbe Muster wie
  // `invalid_credentials`: ein Tippfehler, kein Fehler.
  //
  // NICHT mit `otp_disabled` verwechseln — das hieße, die Anmeldung per Code
  // ist im Projekt abgeschaltet, und das gehört gesehen.
  'otp_expired',
])

const KNOWN_USER_ERROR_PATTERNS: readonly RegExp[] = [
  /different from the old password/i,
  /auth session missing/i,
]

/**
 * Fehler, bei denen die Anfrage das Netz gar nicht erst verlassen hat.
 *
 * `AuthRetryableFetchError` heißt so, weil das Supabase-SDK ihn selbst für
 * wiederholbar hält — es wirft ihn mit Statuscode 0, also ohne jede Antwort:
 * WLAN weg, Rechner aus dem Schlaf, Supabase kurz nicht erreichbar. Der Nutzer
 * bekommt seine Meldung und versucht es noch einmal; im Issue-Stream bliebe
 * nur Rauschen, das echte Meldungen zudeckt.
 *
 * Abgegrenzt gegen echte Serverfehler: Ein **5xx** kommt an, wird gemeldet und
 * gehört gesehen — ein Auth-Hook, der 502 liefert, ist ein Problem.
 */
const TRANSIENT_NETWORK_ERROR = /AuthRetryableFetchError/i

/**
 * Report a Supabase auth error to Sentry — but only if it isn't a known
 * user-side mistake. Known errors are filtered to keep signal-to-noise high.
 *
 * Pair with `mapAuthErrorToI18nKey()` (renders the user-facing message) at
 * each `{ error }` / `catch (error)` site in `auth.ts`. The two helpers do
 * complementary jobs: this one decides whether to alert dev, that one decides
 * what to show the user.
 *
 * @param error - The error object (Supabase `AuthError`, network exception,
 *   or anything thrown). Passed through `Sentry.captureException` as-is.
 * @param operation - Name of the calling auth function (`login`,
 *   `updatePassword`, …). Surfaces in Sentry as a tag for filtering.
 */
export function reportSupabaseAuthError(error: unknown, operation: string): void {
  const err = error as { code?: string; message?: string; name?: string } | null | undefined
  const code = err?.code ?? ''
  const message = err?.message ?? ''
  const name = err?.name ?? ''

  if (KNOWN_USER_ERROR_CODES.has(code)) return
  if (name === 'AuthSessionMissingError') return
  if (KNOWN_USER_ERROR_PATTERNS.some(p => p.test(message))) return
  if (TRANSIENT_NETWORK_ERROR.test(name) || TRANSIENT_NETWORK_ERROR.test(message)) return

  Sentry.captureException(error, {
    tags: {
      source: 'supabase-auth',
      operation,
      ...(code ? { auth_error_code: code } : {}),
    },
  })
}
